import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MDBSpinner } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import { api } from "../../services";
import {
  buildCartFromPlan,
  channelDisplayName,
  clearPartnerSession,
  itemKey,
  partnerConnectReturnPath,
  partnerItemLogoApp,
  readPartnerSession,
  signedInAccountId,
  writePartnerSession,
} from "../../utils/partnerSession";
import { handlePlatformLogoError, resolveIntegrationLogo } from "../../utils/platformLogos";
import { formatMinor } from "../subscription/subscriptionCatalog";
import PartnerConnectItem from "./PartnerConnectItem";
import "./PartnerSessionFlow.css";

function PartnerAppLogo({ item, className = "" }) {
  const app = partnerItemLogoApp(item);
  const src = resolveIntegrationLogo(app) || item?.logo || "";
  if (!src) return null;
  return (
    <img
      className={className}
      src={src}
      alt=""
      onError={(event) => handlePlatformLogoError(event, app)}
    />
  );
}

const STEPS = ["approve", "pay", "connect", "done"];

function textFor(value, language) {
  if (!value) return "";
  if (typeof value === "string") return value;
  const arabic = String(language || "").toLowerCase().startsWith("ar");
  return (arabic ? value.ar : value.en) || value.en || value.ar || "";
}

function initials(name) {
  const parts = String(name || "P").trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "P";
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

async function postComplete(sessionId, body) {
  const res = await api.post(`/partner-sessions/${sessionId}/complete`, body, {
    headers: authHeaders(),
  });
  return res.data;
}

function Stepper({ step, needsPayment }) {
  const { t } = useTranslation("translation");
  const activeIdx = STEPS.indexOf(step);
  return (
    <ol className="psf-steps" aria-label={t("partnerFlow.stepsLabel")}>
      {STEPS.map((id, idx) => {
        const skipped = id === "pay" && !needsPayment && activeIdx > 1;
        const done = idx < activeIdx || skipped;
        const on = idx === activeIdx;
        return (
          <li
            key={id}
            className={`psf-step${on ? " is-on" : ""}${done ? " is-done" : ""}`}
            aria-current={on ? "step" : undefined}
          >
            <span className="n">{done ? "✓" : idx + 1}</span>
            {t(`partnerFlow.steps.${id}`)}
            {skipped ? ` · ${t("partnerFlow.notNeeded")}` : ""}
          </li>
        );
      })}
    </ol>
  );
}

function modeHint(item, t) {
  const key = String(item?.channelKey || "").toLowerCase();
  const mode = String(item?.connectionMode || "");
  if (mode === "oauth" || key === "keeta") return t("partnerFlow.modeOauth", { app: item.channelName || "Keeta" });
  if (mode === "shop_sync") return t("partnerFlow.modeOauthGeneric");
  if (key === "thechefz") return t("partnerFlow.modeChefz");
  if (key === "ninja") return t("partnerFlow.modeNinja");
  if (mode === "addon_toggle") return t("partnerFlow.modeAddon");
  return t("partnerFlow.modeCredentials");
}

function ConnectTypePanel({ session, id, language }) {
  const { t } = useTranslation("translation");
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [branchRefs, setBranchRefs] = useState([]);
  const [allBranches, setAllBranches] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/partner-sessions/connect-options", { headers: authHeaders() });
        if (cancelled) return;
        const next = res.data?.stores || [];
        setStores(next);
        if (next[0]) setStoreId(next[0].id);
      } catch (err) {
        toast.error(err.response?.data?.message || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const branches = useMemo(
    () => stores.find((store) => store.id === storeId)?.branches || [],
    [stores, storeId]
  );

  const leave = (url) => { if (url) window.location.assign(url); };

  const postDecision = async (body) => {
    const res = await api.post(`/partner-sessions/${id}/decision`, body, { headers: authHeaders() });
    return res.data;
  };

  const onAllow = async () => {
    setBusy(true);
    try {
      const result = await postDecision({
        decision: "allow",
        all_branches: allBranches,
        branch_refs: allBranches ? undefined : branchRefs,
      });
      leave(result?.redirect_url);
    } catch (err) {
      toast.error(err.response?.data?.message || t("partnerSessionExpired"));
      setBusy(false);
    }
  };

  const onDecline = async () => {
    setBusy(true);
    try {
      const result = await postDecision({ decision: "decline" });
      leave(result?.redirect_url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="psf-card">
      <h1>{session.partner?.name}</h1>
      <p className="psf-lead">{textFor(session.action, language)}</p>
      <div style={{ display: "grid", gap: 8 }}>
        <select value={storeId} onChange={(e) => { setStoreId(e.target.value); setBranchRefs([]); }}>
          {stores.map((store) => (
            <option key={store.id} value={store.id}>{store.name}</option>
          ))}
        </select>
        <label>
          <input type="checkbox" checked={allBranches} onChange={(e) => setAllBranches(e.target.checked)} />
          {" "}{t("partnerSessionAllBranches")}
        </label>
        {!allBranches ? branches.map((branch) => (
          <label key={branch.ref}>
            <input
              type="checkbox"
              checked={branchRefs.includes(branch.ref)}
              onChange={(e) => {
                setBranchRefs((cur) => (
                  e.target.checked ? [...cur, branch.ref] : cur.filter((ref) => ref !== branch.ref)
                ));
              }}
            />
            {" "}{branch.name}
          </label>
        )) : null}
      </div>
      <div className="psf-actions">
        <button type="button" className="psf-btn" disabled={busy} onClick={onDecline}>
          {t("partnerSessionDecline")}
        </button>
        <button
          type="button"
          className="psf-btn primary"
          disabled={busy || (!allBranches && branchRefs.length === 0)}
          onClick={onAllow}
        >
          {t("partnerSessionAllow")}
        </button>
      </div>
    </div>
  );
}

export default function PartnerSessionFlow() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation("translation");

  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(() => searchParams.get("step") || "approve");
  const [payMode, setPayMode] = useState("custom");
  const [globalCode, setGlobalCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [itemStatus, setItemStatus] = useState({});
  const [paidAt, setPaidAt] = useState(null);
  const [cancelUntil, setCancelUntil] = useState(null);
  const [paidAmount, setPaidAmount] = useState(null);
  /** Pay UX phases matching HTML: redirecting | confirming | notpaid | null */
  const [payPhase, setPayPhase] = useState(null);
  const paymentReturnHandled = useRef(false);

  const session = plan?.session;
  const items = plan?.items || [];
  const needsPayment = Boolean(plan?.needsPayment);
  const partnerName = session?.partner?.name || t("partnerFlow.partnerFallback");
  const wrongAccount = Boolean(
    session?.account_id
    && signedInAccountId()
    && String(session.account_id) !== signedInAccountId()
  );

  const persist = useCallback((patch) => {
    const prev = readPartnerSession() || {};
    writePartnerSession({
      ...prev,
      id,
      partnerName,
      coupon: plan?.coupon || session?.coupon || "",
      returnUrl: session?.return_url || null,
      items,
      step,
      itemStatus,
      paidAt,
      cancelUntil,
      paymentId: prev.paymentId || null,
      ...patch,
    });
  }, [id, partnerName, plan, session, items, step, itemStatus, paidAt, cancelUntil]);

  const loadPlan = useCallback(async () => {
    const res = await api.get(`/partner-sessions/${id}/plan`, { headers: authHeaders() });
    setPlan(res.data);
    return res.data;
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await loadPlan();
        if (cancelled) return;
        if (data.session?.type === "connect") return;
        const stored = readPartnerSession();
        const queryStep = searchParams.get("step");
        if (queryStep && STEPS.includes(queryStep)) {
          setStep(queryStep);
        } else if (stored?.id === id && stored.step) {
          setStep(stored.step);
        }
        if (stored?.id === id) {
          if (stored.itemStatus) setItemStatus(stored.itemStatus);
          if (stored.paidAt) setPaidAt(stored.paidAt);
          if (stored.cancelUntil) setCancelUntil(stored.cancelUntil);
          if (stored.paidAmount != null) setPaidAmount(stored.paidAmount);
        }
        const nextStatus = {};
        (data.items || []).forEach((item, idx) => {
          const key = itemKey(item, idx);
          nextStatus[key] = stored?.itemStatus?.[key] || "to_connect";
        });
        setItemStatus((prev) => ({ ...nextStatus, ...prev }));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || t("partnerSessionExpired"));
      }
    })();
    return () => { cancelled = true; };
  }, [id, loadPlan, searchParams, t]);

  useEffect(() => {
    if (!plan || plan.session?.type === "connect") return;
    persist({});
  }, [plan, step, itemStatus, paidAt, cancelUntil, persist]);

  useEffect(() => {
    if (!needsPayment || payMode !== "custom" || !plan?.suggestedCart?.items?.length) {
      if (payMode === "global" && globalCode) {
        // quote loaded below
      } else if (!needsPayment) {
        setQuote(null);
        return;
      }
    }
    let cancelled = false;
    (async () => {
      if (step !== "pay" && step !== "approve") return;
      const cart = buildCartFromPlan(plan, payMode, globalCode);
      if (!cart.length) {
        setQuote(null);
        return;
      }
      try {
        const res = await api.post("/subscriptions/bundle/quote", {
          items: cart,
          couponCode: plan?.coupon || undefined,
          previewChange: Boolean(plan?.hasSubscription),
        }, { headers: authHeaders() });
        if (!cancelled) setQuote(res.data);
      } catch (err) {
        if (!cancelled) setQuote(null);
      }
    })();
    return () => { cancelled = true; };
  }, [plan, payMode, globalCode, needsPayment, step]);

  const goStep = (next) => {
    setStep(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("step", next);
      return p;
    }, { replace: true });
  };

  const leave = (url) => {
    if (url) window.location.assign(url);
  };

  const onCancelSession = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/partner-sessions/${id}/decision`, { decision: "cancel" }, { headers: authHeaders() });
      clearPartnerSession();
      leave(res.data?.redirect_url || session?.return_url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  const onApproveContinue = async () => {
    if (wrongAccount) return;
    setBusy(true);
    try {
      await api.post(
        `/partner-sessions/${id}/decision`,
        { decision: "continue" },
        { headers: authHeaders() }
      );
      // Stay in the focused shell (Approve → Pay/Connect). Partner redirect_url is used on Done/Cancel.
      goStep(needsPayment ? "pay" : "connect");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPay = async () => {
    setBusy(true);
    setPayPhase("redirecting");
    try {
      const cart = buildCartFromPlan(plan, payMode, globalCode);
      if (!cart.length) {
        toast.error(t("partnerFlow.noCart"));
        setPayPhase(null);
        setBusy(false);
        return;
      }
      const returnUrl = `${window.location.origin}${partnerConnectReturnPath(id)}&paid=1`;
      const couponCode = plan?.coupon || undefined;

      const amountDue = quote?.due_today ?? quote?.dueToday ?? quote?.total ?? quote?.amount ?? null;
      if (plan?.hasSubscription) {
        await api.post("/subscriptions/bundle/apply", { items: cart }, { headers: authHeaders() });
        const now = Date.now();
        setPaidAt(now);
        setPaidAmount(amountDue);
        setCancelUntil(now + 2 * 60 * 60 * 1000);
        persist({
          step: "connect",
          paidAt: now,
          paidAmount: amountDue,
          cancelUntil: now + 2 * 60 * 60 * 1000,
        });
        setPayPhase(null);
        goStep("connect");
        setBusy(false);
        return;
      }

      const res = await api.post("/subscriptions/bundle/checkout", {
        items: cart,
        couponCode,
        returnUrl,
      }, { headers: authHeaders() });

      const checkoutUrl = res.data?.checkoutUrl || res.data?.subscription?.checkoutUrl;
      const paymentId = res.data?.subscription?.latestPaymentId || null;
      const now = Date.now();
      persist({
        step: "pay",
        awaitingPayment: true,
        paymentId,
        paidAt: now,
        paidAmount: amountDue,
        cancelUntil: now + 2 * 60 * 60 * 1000,
      });
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      setPaidAt(now);
      setPaidAmount(amountDue);
      setCancelUntil(now + 2 * 60 * 60 * 1000);
      setPayPhase(null);
      goStep("connect");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setPayPhase(null);
      setBusy(false);
    }
  };

  const onCancelPayment = async () => {
    setBusy(true);
    try {
      const stored = readPartnerSession();
      const res = await api.post(
        `/partner-sessions/${id}/cancel-payment`,
        { paymentId: stored?.paymentId || undefined },
        { headers: authHeaders() }
      );
      clearPartnerSession();
      leave(res.data?.redirect_url || session?.return_url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  const markItem = (key, status) => {
    setItemStatus((prev) => ({ ...prev, [key]: status }));
  };

  const finish = async () => {
    setBusy(true);
    try {
      const links = items.map((item, idx) => {
        const status = itemStatus[itemKey(item, idx)] || "pending";
        return {
          branch_id: item.branchId || null,
          channel: item.channelKey || item.channelName,
          link_status: status === "live" ? "live" : "pending",
        };
      });
      const allLive = links.length > 0 && links.every((l) => l.link_status === "live");
      const result = await postComplete(id, {
        status: allLive ? "completed" : "pending",
        links,
      });
      clearPartnerSession();
      goStep("done");
      setTimeout(() => {
        leave(result?.redirect_url || session?.return_url);
      }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  const backToPartner = async () => {
    setBusy(true);
    try {
      const links = items.map((item, idx) => {
        const status = itemStatus[itemKey(item, idx)] || "pending";
        return {
          branch_id: item.branchId || null,
          channel: item.channelKey || item.channelName,
          link_status: status === "live" ? "live" : "pending",
        };
      });
      const allLive = links.length > 0 && links.every((l) => l.link_status === "live");
      const result = await postComplete(id, {
        status: allLive ? "completed" : "pending",
        links,
      });
      clearPartnerSession();
      leave(result?.redirect_url || session?.return_url);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  // Mark live when returning from AddApp with partnerSession query handled via storage
  useEffect(() => {
    const stored = readPartnerSession();
    if (!stored?.id || stored.id !== id) return;
    if (stored.linkStatus === "live" && stored.connectingKey) {
      markItem(stored.connectingKey, "live");
      writePartnerSession({ ...stored, linkStatus: undefined, connectingKey: undefined });
    }
  }, [id, step]);

  // Return from Loops Payments: confirm briefly, then Connect — or show not-paid if abandoned.
  useEffect(() => {
    if (!plan || plan.session?.type === "connect") return;
    const paidFlag = searchParams.get("paid");
    const paymentFlag = searchParams.get("payment");
    const stored = readPartnerSession();

    if (paidFlag === "0" || paymentFlag === "cancelled") {
      paymentReturnHandled.current = false;
      setPayPhase("notpaid");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("paid");
        next.delete("payment");
        next.set("step", "pay");
        return next;
      }, { replace: true });
      setStep("pay");
      return;
    }

    const shouldConfirm = paidFlag === "1"
      || (stored?.id === id && stored.awaitingPayment && (step === "connect" || step === "pay"));
    if (!shouldConfirm || paymentReturnHandled.current) return;

    paymentReturnHandled.current = true;
    setPayPhase("confirming");
    setPaidAt(stored?.paidAt || Date.now());
    setPaidAmount(stored?.paidAmount ?? null);
    setCancelUntil(stored?.cancelUntil || Date.now() + 2 * 60 * 60 * 1000);
    const timer = setTimeout(() => {
      writePartnerSession({
        ...(readPartnerSession() || {}),
        id,
        awaitingPayment: false,
        paidAt: stored?.paidAt || Date.now(),
        paidAmount: stored?.paidAmount ?? null,
        cancelUntil: stored?.cancelUntil || Date.now() + 2 * 60 * 60 * 1000,
        step: "connect",
      });
      setPayPhase(null);
      setStep("connect");
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("paid");
        next.set("step", "connect");
        return next;
      }, { replace: true });
    }, 1200);
    return () => clearTimeout(timer);
  }, [plan, id, step, searchParams, setSearchParams]);

  if (error) {
    return (
      <div className="psf-page" dir={i18n.dir()} lang={i18n.language}>
        <div className="psf-shell"><div className="psf-card"><p>{error}</p></div></div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="psf-page" dir={i18n.dir()} lang={i18n.language}>
        <div className="psf-shell" style={{ justifyItems: "center", paddingTop: 80 }}>
          <MDBSpinner role="status" />
        </div>
      </div>
    );
  }

  if (session?.type === "connect") {
    return (
      <div className="psf-page" dir={i18n.dir()} lang={i18n.language}>
        <div className="psf-shell">
          <div className="psf-req">
            <span className="psf-partner-mark">{initials(partnerName)}</span>
            <span className="lead"><b>{partnerName}</b></span>
          </div>
          <ConnectTypePanel session={session} id={id} language={i18n.language} />
        </div>
      </div>
    );
  }

  const isAr = String(i18n.language || "").toLowerCase().startsWith("ar");
  const channelLabels = [...new Set(items.map((i) => channelDisplayName(i)).filter(Boolean))];
  const branchLabels = [...new Set(items.map((i) => i.branchName).filter(Boolean))];
  const appsText = channelLabels.join(t("partnerFlow.andSep"));
  const branchText = branchLabels.join(t("partnerFlow.andSep")) || "—";
  const branchCount = Math.max(1, branchLabels.length);
  const doneCount = items.filter((item, idx) => {
    const st = itemStatus[itemKey(item, idx)];
    return st === "live" || st === "pending";
  }).length;
  const pendingCount = items.filter((item, idx) => itemStatus[itemKey(item, idx)] !== "live").length;
  const allLive = items.length > 0 && pendingCount === 0;
  // Unique apps × branches for Done matrix (HTML prototype table).
  const doneChannels = [...new Map(items.map((item) => [item.channelKey || item.appId, item])).values()];
  const doneBranches = [...new Set(items.map((i) => i.branchName || i.branchRef || i.branchId).filter(Boolean))];
  const due = quote?.due_today ?? quote?.dueToday ?? quote?.total ?? quote?.amount ?? null;
  const vat = quote?.due_today_tax ?? quote?.tax_amount ?? quote?.vat ?? quote?.tax ?? null;
  // Prefer due − VAT so "Subtotal before VAT" matches the prototype (Payments `subtotal`
  // can equal the VAT-inclusive `amount` depending on quote shape).
  const subtotal = quote?.due_today_subtotal
    ?? (due != null && vat != null ? Math.max(0, Number(due) - Number(vat)) : null)
    ?? quote?.subtotal
    ?? null;
  const timeLocale = isAr ? "ar-SA" : undefined;
  const fmtTime = (ms) => (ms
    ? new Date(ms).toLocaleTimeString(timeLocale, { hour: "2-digit", minute: "2-digit" })
    : "");

  // Pay table: one row per app (prototype). Prices come from Quote API items
  // `{ plan_code, quantity, subtotal }` matched via suggestedCart featureKey → planCode.
  const payRows = (() => {
    const quoteByPlan = new Map();
    const quoteLines = [
      ...(Array.isArray(quote?.items) ? quote.items : []),
      ...(Array.isArray(quote?.due_today_items) ? quote.due_today_items : []),
      ...(Array.isArray(quote?.lines) ? quote.lines : []),
    ];
    quoteLines.forEach((line) => {
      const code = line?.plan_code || line?.planCode;
      if (!code || quoteByPlan.has(String(code))) return;
      quoteByPlan.set(String(code), line);
    });

    const cartItems = plan.suggestedCart?.items || [];
    const cartByFeature = new Map(
      cartItems
        .filter((row) => row?.featureKey && row?.planCode)
        .map((row) => [row.featureKey, row])
    );
    const unusedCart = cartItems.filter((row) => row?.planCode);

    const lineAmounts = (line, cartRow) => {
      const qty = Number(line?.quantity ?? cartRow?.quantity ?? 1) || 1;
      const monthRaw = line?.subtotal
        ?? line?.amount
        ?? line?.total_amount
        ?? line?.price
        ?? (cartRow?.unitAmount != null ? Number(cartRow.unitAmount) * qty : null)
        ?? null;
      const unitRaw = line?.unit_amount
        ?? line?.unitAmount
        ?? cartRow?.unitAmount
        ?? (monthRaw != null ? Math.round(Number(monthRaw) / qty) : null);
      return {
        unit: unitRaw != null ? Number(unitRaw) : null,
        month: monthRaw != null ? Number(monthRaw) : null,
      };
    };

    const byChannel = new Map();
    items.forEach((item, idx) => {
      const key = item.channelKey || item.appId || String(idx);
      if (!byChannel.has(key)) {
        let cartRow = (item.featureKey && cartByFeature.get(item.featureKey)) || null;
        if (cartRow) {
          const at = unusedCart.findIndex((row) => row.planCode === cartRow.planCode && row.featureKey === cartRow.featureKey);
          if (at >= 0) unusedCart.splice(at, 1);
        } else if (unusedCart.length) {
          cartRow = unusedCart.shift();
        }
        const line = cartRow?.planCode ? quoteByPlan.get(String(cartRow.planCode)) : null;
        const amounts = lineAmounts(line, cartRow);
        byChannel.set(key, {
          item,
          branches: [],
          unit: amounts.unit,
          month: amounts.month,
        });
      }
      const row = byChannel.get(key);
      if (item.branchName && !row.branches.includes(item.branchName)) {
        row.branches.push(item.branchName);
      }
    });
    return [...byChannel.values()];
  })();

  const selectedGlobal = (plan.globalPlans || []).find((g) => g.code === globalCode) || null;
  const customDue = due;
  const displayDue = payMode === "global" && selectedGlobal?.amount != null
    ? selectedGlobal.amount
    : customDue;

  const paidBanner = (paidAt || cancelUntil) ? (
    <div className="psf-banner-pay">
      <span>
        {t("partnerFlow.paidBanner", {
          time: fmtTime(paidAt),
          until: fmtTime(cancelUntil),
          amount: (paidAmount != null || due != null)
            ? formatMinor(paidAmount ?? due, i18n.language)
            : "",
        })}
      </span>
      {cancelUntil && Date.now() < cancelUntil ? (
        <button type="button" className="psf-link" disabled={busy} onClick={onCancelPayment}>
          {t("partnerFlow.cancelPayment")}
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div className="psf-page" dir={i18n.dir()} lang={i18n.language}>
      <div className="psf-shell">
        <div className="psf-req">
          <span className="psf-partner-mark" aria-hidden="true">{initials(partnerName)}</span>
          <p className="lead">
            <span className="psf-req-mute">{t("partnerFlow.requestedBy")}</span>
            {" "}
            <b>{partnerName}</b>
            <span className="psf-req-mute"> · {t("partnerFlow.connectVerb")} </span>
            <b>{appsText}</b>
            <span className="psf-req-mute"> {t("partnerFlow.connectTo")} </span>
            <b>{branchText}</b>
          </p>
          <button type="button" className="psf-back-link" disabled={busy} onClick={backToPartner}>
            {t("partnerFlow.backToPartner", { partner: partnerName })}
          </button>
        </div>

        <Stepper step={step} needsPayment={needsPayment} />

        {payPhase === "redirecting" || payPhase === "confirming" ? (
          <div className="psf-card">
            <div className="psf-wait">
              <div className="psf-spin" aria-hidden="true" />
              <h2>
                {payPhase === "redirecting"
                  ? t("partnerFlow.payRedirectingTitle")
                  : t("partnerFlow.payConfirmingTitle")}
              </h2>
              <p className="psf-hint">
                {payPhase === "redirecting"
                  ? t("partnerFlow.payRedirectingHint")
                  : t("partnerFlow.payConfirmingHint")}
              </p>
            </div>
          </div>
        ) : null}

        <div className="psf-card psf-flow" hidden={payPhase === "redirecting" || payPhase === "confirming"}>
          {step === "approve" ? (
            <>
            <div className="psf-pair">
              <span className="psf-partner-mark lg" aria-hidden="true">{initials(partnerName)}</span>
              <span className="psf-pair-link" aria-hidden="true">⇄</span>
              <span className="psf-pair-logos">
                {[...new Map(items.map((item) => [item.channelKey || item.appId, item])).values()].map((item) => (
                  <PartnerAppLogo key={item.channelKey || item.appId} item={item} />
                ))}
              </span>
            </div>
            <h2 className="psf-title">
              {t(
                branchLabels.length > 1
                  ? "partnerFlow.approveTitleMany"
                  : "partnerFlow.approveTitle",
                {
                  partner: partnerName,
                  apps: appsText,
                  branch: branchText,
                }
              )}
            </h2>
            <p className="psf-sub">
              {t(
                channelLabels.length > 1
                  ? "partnerFlow.approveLeadMany"
                  : "partnerFlow.approveLead",
                { partner: partnerName, apps: appsText, branch: branchText }
              )}
            </p>
            {wrongAccount ? <div className="psf-warn">{t("partnerSessionWrongAccount")}</div> : null}

            {needsPayment ? (
              <div style={{ display: "grid", gap: 10 }}>
                <div className="psf-label">{t("partnerFlow.chooseHowToPay")}</div>
                <div className="psf-plans" role="group" aria-label={t("partnerFlow.justTheseApps")}>
                  <button
                    type="button"
                    className={`psf-plan${payMode === "custom" ? " is-on" : ""}`}
                    aria-pressed={payMode === "custom"}
                    onClick={() => setPayMode("custom")}
                  >
                    <span className="radio" aria-hidden="true" />
                    <b>{t("partnerFlow.justTheseApps")}</b>
                    <span>
                      {t("partnerFlow.appsAtBranch", { apps: appsText, branch: branchText })}
                    </span>
                    {customDue != null ? (
                      <span className="plan-price">
                        {formatMinor(customDue, i18n.language)}
                        <small>{t("partnerFlow.perMonth")}</small>
                      </span>
                    ) : null}
                  </button>
                </div>

                {(plan.globalPlans || []).length ? (
                  <>
                    <p className="psf-cover-hint">
                      <b>{t("partnerFlow.orCoverAccountBold")}</b>
                      {" "}
                      {t("partnerFlow.orCoverAccountBody")}
                    </p>
                    <div className="psf-plans is-global" role="group" aria-label={t("partnerFlow.globalPlans")}>
                      {plan.globalPlans.map((g, idx) => {
                        const limit = Number(g.branchLimit || 0);
                        const tooSmall = limit > 0 && limit < branchCount;
                        const costsLess = customDue != null && g.amount != null && Number(g.amount) < Number(customDue);
                        const popular = idx === 1 && !tooSmall && !costsLess;
                        return (
                          <button
                            type="button"
                            key={g.code}
                            className={`psf-plan${payMode === "global" && globalCode === g.code ? " is-on" : ""}`}
                            aria-pressed={payMode === "global" && globalCode === g.code}
                            disabled={tooSmall}
                            onClick={() => {
                              if (tooSmall) return;
                              setPayMode("global");
                              setGlobalCode(g.code);
                            }}
                          >
                            <span className="radio" aria-hidden="true" />
                            <b>
                              {g.name || g.code}
                              {tooSmall ? <span className="psf-pill na">{t("partnerFlow.notAvailable")}</span> : null}
                              {!tooSmall && costsLess ? <span className="psf-pill teal">{t("partnerFlow.costsLess")}</span> : null}
                              {!tooSmall && popular ? <span className="psf-pill hot">{t("partnerFlow.mostPopular")}</span> : null}
                            </b>
                            <span>
                              {limit
                                ? t("partnerFlow.everyAppUpTo", { count: limit })
                                : t("partnerFlow.everyAppUnlimited")}
                            </span>
                            {tooSmall ? (
                              <span className="why">
                                {t("partnerFlow.globalTooSmall", {
                                  limit,
                                  needed: branchCount,
                                  partner: partnerName,
                                })}
                              </span>
                            ) : null}
                            {g.amount != null ? (
                              <span className="plan-price">
                                {formatMinor(g.amount, i18n.language)}
                                <small>{t("partnerFlow.perMonth")}</small>
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}

            <div className="psf-cols">
              <div className="psf-box">
                <h3>{t("partnerFlow.partnerWillBeAbleNamed", { partner: partnerName })}</h3>
                <ul className="psf-perks">
                  <li>{t("partnerFlow.abilityPublishDetailed", { apps: appsText, branch: branchText })}</li>
                  <li>{t("partnerFlow.abilityOrders")}</li>
                  <li>{t("partnerFlow.abilityStatus")}</li>
                </ul>
              </div>
              <div className="psf-box">
                <h3>{t("partnerFlow.whatItCosts")}</h3>
                {needsPayment && displayDue != null ? (
                  <>
                    <div className="psf-price">
                      {formatMinor(displayDue, i18n.language)}
                      <small>{t("partnerFlow.perMonthVat")}</small>
                    </div>
                    <p className="psf-hint">
                      {payMode === "global" && selectedGlobal
                        ? t("partnerFlow.costPlanGlobal", {
                          plan: selectedGlobal.name || selectedGlobal.code,
                          apps: appsText,
                          count: selectedGlobal.branchLimit || branchCount,
                        })
                        : t("partnerFlow.costPlanCustom", {
                          apps: appsText,
                          branch: branchText,
                        })}
                      {" "}
                      {t("partnerFlow.costPayFirst")}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="psf-price">{t("partnerFlow.costIncluded")}</div>
                    <p className="psf-hint">{t("partnerFlow.costAlreadyCovered")}</p>
                  </>
                )}
              </div>
            </div>

            <div className="psf-warn">
              <b>{t("partnerFlow.beforeContinueTitle")}</b>
              <span>{t("partnerFlow.beforeContinue")}</span>
            </div>

            <div className="psf-actions">
              <button type="button" className="psf-btn" disabled={busy} onClick={onCancelSession}>
                {t("partnerSessionCancel")}
              </button>
              <button
                type="button"
                className="psf-btn primary"
                disabled={busy || wrongAccount || (needsPayment && payMode === "global" && !globalCode)}
                onClick={onApproveContinue}
              >
                {needsPayment ? t("partnerFlow.continueToPayment") : t("partnerFlow.continue")}
              </button>
            </div>
            </>
          ) : null}

          {step === "pay" ? (
            <>
            {payPhase === "notpaid" ? (
              <div className="psf-alert bad" role="alert">
                <b>{t("partnerFlow.paymentNotCompletedTitle")}</b>
                <span>{t("partnerFlow.paymentNotCompletedBody")}</span>
              </div>
            ) : null}
            <div className="psf-section-head">
              <h2>{t("partnerFlow.reviewAndPay")}</h2>
              <span className="meta">{t("partnerFlow.loopsPayments")}</span>
            </div>
            {payMode === "global" && selectedGlobal ? (
              <div className="psf-maprow">
                <div className="head">
                  <span className="psf-app-cell">
                    <b>{selectedGlobal.name || selectedGlobal.code}</b>
                  </span>
                  <span>
                    {selectedGlobal.branchLimit
                      ? t("partnerFlow.upToEveryApp", { count: selectedGlobal.branchLimit })
                      : t("partnerFlow.everyAppUnlimited")}
                  </span>
                </div>
                <p className="psf-hint">
                  {t("partnerFlow.globalCovers", { apps: appsText, branch: branchText })}
                </p>
              </div>
            ) : (
              <div className="psf-tablebox">
                <table className="psf-table">
                  <thead>
                    <tr>
                      <th>{t("partnerFlow.colApp")}</th>
                      <th>{t("partnerFlow.colBranches")}</th>
                      <th className="r">{t("partnerFlow.colPerBranch")}</th>
                      <th className="r">{t("partnerFlow.colPerMonth")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payRows.map((row) => (
                      <tr key={row.item.channelKey || row.item.appId}>
                        <td>
                          <div className="psf-app-cell">
                            <PartnerAppLogo item={row.item} />
                            <span>
                              {channelDisplayName(row.item)}
                              <small>{modeHint(row.item, t)}</small>
                            </span>
                          </div>
                        </td>
                        <td>{row.branches.join(t("partnerFlow.listSep")) || "—"}</td>
                        <td className="r">
                          {row.unit != null ? formatMinor(row.unit, i18n.language) : "—"}
                        </td>
                        <td className="r">
                          {row.month != null ? formatMinor(row.month, i18n.language) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <dl className="psf-totals">
              {subtotal != null && payMode !== "global" ? (
                <div>
                  <dt>{t("partnerFlow.subtotal")}</dt>
                  <dd>{formatMinor(subtotal, i18n.language)}</dd>
                </div>
              ) : null}
              {vat != null && payMode !== "global" ? (
                <div>
                  <dt>{t("partnerFlow.vat")}</dt>
                  <dd>{formatMinor(vat, i18n.language)}</dd>
                </div>
              ) : null}
              {displayDue != null ? (
                <div className="grand">
                  <dt>{t("partnerFlow.dueToday")}</dt>
                  <dd>{formatMinor(displayDue, i18n.language)}</dd>
                </div>
              ) : null}
            </dl>
            <p className="psf-hint">{t("partnerFlow.payThenConnect")}</p>
            <div className="psf-actions">
              <button
                type="button"
                className="psf-btn"
                disabled={busy}
                onClick={() => { setPayPhase(null); goStep("approve"); }}
              >
                {t("partnerFlow.back")}
              </button>
              <button type="button" className="psf-btn primary" disabled={busy} onClick={onPay}>
                {t("partnerFlow.continueToPayment")}
              </button>
            </div>
            </>
          ) : null}

          {step === "connect" ? (
            <>
              {paidBanner}
              <div className="psf-section-head">
                <h2>{t("partnerFlow.connectTitle")}</h2>
                <span className="meta">{t("partnerFlow.connectProgress", { done: doneCount, total: items.length })}</span>
              </div>
              <div className="psf-connect-list">
                {items.map((item, idx) => (
                  <PartnerConnectItem
                    key={itemKey(item, idx)}
                    sessionId={id}
                    item={item}
                    index={idx}
                    status={itemStatus[itemKey(item, idx)] || "to_connect"}
                    onStatus={markItem}
                    busy={busy}
                    setBusy={setBusy}
                  />
                ))}
              </div>
              <div className="psf-actions">
                <span className="psf-hint">
                  {doneCount === items.length
                    ? t("partnerFlow.allSet")
                    : t("partnerFlow.connectFooter")}
                </span>
                <button
                  type="button"
                  className="psf-btn primary"
                  disabled={
                    busy
                    || !items.length
                    || items.some((it, i) => {
                      const st = itemStatus[itemKey(it, i)] || "to_connect";
                      return st !== "live" && st !== "pending";
                    })
                  }
                  onClick={finish}
                >
                  {t("partnerFlow.finish")}
                </button>
              </div>
            </>
          ) : null}

          {step === "done" ? (
            <>
              <div className="psf-done">
                <div className={`psf-done-mark${allLive ? "" : " warn"}`}>{allLive ? "✓" : "!"}</div>
                <h2>{allLive ? t("partnerFlow.doneAllLive") : t("partnerFlow.doneSomePending")}</h2>
              </div>
              <div className="psf-tablebox">
                <table className="psf-table">
                  <thead>
                    <tr>
                      <th>{t("partnerFlow.colApp")}</th>
                      {(doneBranches.length ? doneBranches : ["—"]).map((br) => (
                        <th key={br}>{br}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {doneChannels.map((channel) => {
                      const channelItems = items
                        .map((item, idx) => ({ item, idx, key: itemKey(item, idx) }))
                        .filter(({ item }) => (
                          (item.channelKey || item.appId) === (channel.channelKey || channel.appId)
                        ));
                      const branches = doneBranches.length ? doneBranches : ["—"];
                      return (
                        <tr key={channel.channelKey || channel.appId}>
                          <td>
                            <div className="psf-app-cell">
                              <PartnerAppLogo item={channel} />
                              <span>{channelDisplayName(channel)}</span>
                            </div>
                          </td>
                          {branches.map((br) => {
                            const hit = channelItems.find(({ item }) => (
                              (item.branchName || item.branchRef || item.branchId || "—") === br
                            ));
                            const st = hit ? (itemStatus[hit.key] || "pending") : "pending";
                            const live = st === "live";
                            return (
                              <td key={`${channel.channelKey}-${br}`}>
                                <span className={`psf-tag ${live ? "add" : "less"}`}>
                                  {live ? t("partnerFlow.tagLive") : t("partnerFlow.tagConnectLater")}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {!allLive ? (
                <p className="psf-hint">{t("partnerFlow.donePendingHint", { partner: partnerName })}</p>
              ) : null}
              {paidBanner}
              <div className="psf-actions">
                {allLive ? (
                  <span className="psf-hint">
                    {t("partnerFlow.takingYouBack", { partner: partnerName })}
                  </span>
                ) : (
                  <button type="button" className="psf-btn" onClick={() => { window.location.assign("/apps-management"); }}>
                    {t("partnerFlow.openApps")}
                  </button>
                )}
                <button type="button" className="psf-btn navy" disabled={busy} onClick={backToPartner}>
                  {allLive
                    ? t("partnerFlow.backToPartnerNow", { partner: partnerName })
                    : t("partnerFlow.backToPartner", { partner: partnerName })}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
